import { requestWorkspaceExit } from '../core/workspace/useWorkspaceDraftGuard';
// src/context/AuthContext.jsx
import React, {
  createContext, useEffect, useRef, useMemo, useState, useCallback,
} from "react";
import api, { logout as apiLogout, logoutAll as apiLogoutAll } from "../api";
import { getJwtExp, isJwtExpired, accessUserId } from "../utils/auth";

export const AuthContext = createContext();

const REFRESH_BEFORE_MS = 2 * 60 * 1000;
const AUTH_CHANNEL = "thedoar-auth-v1";
const PEER_TOKEN_WAIT_MS = 350;
const SIGNED_OUT_KEY = "doar_signed_out";

function validPeerToken(value) {
  return typeof value === "string" && value.length > 20 && !isJwtExpired(value);
}

function requestPeerAccessToken() {
  if (typeof BroadcastChannel === "undefined") return Promise.resolve(null);
  return new Promise((resolve) => {
    const channel = new BroadcastChannel(AUTH_CHANNEL);
    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      channel.close();
      resolve(value);
    };
    channel.onmessage = (event) => {
      if (event.data?.type === "auth:logout") finish(null);
      if (event.data?.type === "auth:token" && validPeerToken(event.data.token)) finish(event.data.token);
    };
    const timer = setTimeout(() => finish(null), PEER_TOKEN_WAIT_MS);
    channel.postMessage({ type: "auth:request" });
  });
}

function broadcastAuth(message) {
  if (typeof BroadcastChannel === "undefined") return;
  try {
    const channel = new BroadcastChannel(AUTH_CHANNEL);
    channel.postMessage(message);
    channel.close();
  } catch {}
}

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const refreshTimerRef = useRef(null);

  function scheduleRefresh(accessToken) {
    clearTimeout(refreshTimerRef.current);
    const exp = getJwtExp(accessToken);
    if (!exp) return;
    const delay = exp * 1000 - Date.now() - REFRESH_BEFORE_MS;
    refreshTimerRef.current = setTimeout(silentRefresh, Math.max(0, delay));
  }

  function applyAccessToken(access) {
    if (!validPeerToken(access)) return false;
    sessionStorage.removeItem(SIGNED_OUT_KEY);
    sessionStorage.setItem("doar_token", access);
    // A newly selected account must not display the previous owner's profile
    // while its own /auth/me request is pending or temporarily unavailable.
    const accountId = accessUserId(access);
    setUserInfo(current => current &&
      String(current.id || current._id || "").toLowerCase() === accountId ? current : null);
    setToken(access);
    scheduleRefresh(access);
    return true;
  }

  function _clearLocalAuth(markSignedOut = false) {
    clearTimeout(refreshTimerRef.current);
    sessionStorage.removeItem("doar_token");
    sessionStorage.removeItem("doar_refresh");
    localStorage.removeItem("doar_token");
    localStorage.removeItem("token");
    if (markSignedOut) sessionStorage.setItem(SIGNED_OUT_KEY, "1");
    setToken(null);
    setUserInfo(null);
  }

  const silentRefresh = useCallback(async () => {
    if (sessionStorage.getItem(SIGNED_OUT_KEY) === "1") return;
    const original = sessionStorage.getItem("doar_token");
    const accountId = accessUserId(original);
    // Never let an old refresh request select a different browser account.
    const stillSelected = () => sessionStorage.getItem(SIGNED_OUT_KEY) !== "1" &&
      sessionStorage.getItem("doar_token") === original;
    try {
      const { data } = await api.post("/auth/refresh", null, {
        ...(accountId ? { headers: { "X-Doar-Account-Id": accountId } } : {}),
        _skipAuthRefresh: true,
      });
      if (!stillSelected()) return;
      const next = data?.token || data?.accessToken;
      if (next && (!accountId || accessUserId(next) === accountId)) applyAccessToken(next);
      // An empty/mismatched refresh reply is not proof of a revoked session.
    } catch (error) {
      if (!stillSelected()) return;
      // 409 is expected when another tab wins refresh rotation; 5xx and
      // transport failures do not revoke an account.
      if (error?.response?.status === 401) _clearLocalAuth(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = useCallback(async (options = {}) => {
    // Capture before the potentially asynchronous unsaved-work dialog:
    // confirming A's logout must never sign out B after an account switch.
    const accessAtLogout = sessionStorage.getItem("doar_token");
    if (!await requestWorkspaceExit()) return false;
    if (sessionStorage.getItem("doar_token") !== accessAtLogout) return false;
    // HttpOnly refresh cookies may be unavailable on another first-party domain.
    _clearLocalAuth(true);
    // A guest/no-session tab must not send an unscoped logout that clears
    // another account's session in a different tab.
    const signingOutId = accessUserId(accessAtLogout);
    if (signingOutId) broadcastAuth({ type: "auth:logout", userId: signingOutId });
    if (options.remote === false) return;
    try { await apiLogout(accessAtLogout); } catch { }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logoutAll = useCallback(async () => {
    const original = sessionStorage.getItem("doar_token");
    if (!await requestWorkspaceExit()) return false;
    if (sessionStorage.getItem("doar_token") !== original) return false;
    // Remote logout-all must target the selected account even if another
    // browser action switches identity while the request is in flight.
    await apiLogoutAll(original);
    const current = sessionStorage.getItem("doar_token");
    // Access-token rotation for the SAME account must not leave a revoked
    // session displayed as signed in. A different account stays untouched.
    if (current !== original &&
        (!original || accessUserId(current) !== accessUserId(original))) return false;
    _clearLocalAuth(true);
    broadcastAuth({ type: "auth:logout", userId: accessUserId(original) });
    return true;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchMe = useCallback(async () => {
    const original = sessionStorage.getItem("doar_token");
    const accountId = accessUserId(original);
    if (!accountId || sessionStorage.getItem(SIGNED_OUT_KEY) === "1") return null;
    const stillSelected = () => sessionStorage.getItem("doar_token") === original &&
      sessionStorage.getItem(SIGNED_OUT_KEY) !== "1";
    try {
      const { data } = await api.get("/auth/me", { headers: { Authorization: `Bearer ${original}` } });
      if (!stillSelected()) return null;
      const user = data?.user || null;
      // Account A's delayed profile must never appear in Account B's UI.
      if (!user || String(user.id || user._id || "").toLowerCase() !== accountId) return null;
      setUserInfo(user);
      return user;
    } catch (error) {
      if (stillSelected() && error?.response?.status === 401) _clearLocalAuth(true);
      return null;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    function handleForceLogout(e) {
      const currentAccount = accessUserId(sessionStorage.getItem("doar_token"));
      const accountId = e.detail?.userId || currentAccount;
      // The response may arrive after the account has switched (or after
      // the API removed the old bearer). Preserve the actual revocation owner.
      if (accountId && currentAccount && accountId !== currentAccount) return;
      console.info("[auth] force-logout triggered:", e.detail?.code);
      _clearLocalAuth(true);
      broadcastAuth({ type: "auth:logout", userId: accountId });
    }
    function handleTokenRefreshed(e) {
      const refreshed = e.detail?.token;
      const accountId = e.detail?.userId;
      const current = sessionStorage.getItem("doar_token");
      if (sessionStorage.getItem(SIGNED_OUT_KEY) === "1" ||
          !accountId || accessUserId(current) !== accountId ||
          current !== refreshed || !validPeerToken(refreshed)) return;
      applyAccessToken(refreshed);
    }
    window.addEventListener("auth:force-logout", handleForceLogout);
    window.addEventListener("auth:token-refreshed", handleTokenRefreshed);
    return () => {
      window.removeEventListener("auth:force-logout", handleForceLogout);
      window.removeEventListener("auth:token-refreshed", handleTokenRefreshed);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") return undefined;
    const channel = new BroadcastChannel(AUTH_CHANNEL);
    channel.onmessage = (event) => {
      if (event.data?.type === "auth:logout") {
        const currentId = accessUserId(sessionStorage.getItem("doar_token"));
        // Legacy/unscoped events must not evict a currently authenticated
        // account. A scoped logout is accepted only for the same owner.
        if (currentId && event.data.userId !== currentId) return;
        _clearLocalAuth(true);
        return;
      }
      if (event.data?.type !== "auth:request") return;
      const access = sessionStorage.getItem("doar_token");
      if (validPeerToken(access) && sessionStorage.getItem(SIGNED_OUT_KEY) !== "1") {
        channel.postMessage({ type: "auth:token", token: access });
      }
    };
    return () => channel.close();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    localStorage.removeItem("doar_token");
    localStorage.removeItem("token");
    sessionStorage.removeItem("doar_refresh");

    const params = new URLSearchParams(window.location.search);
    const handoffCode = params.get("doar_handoff");
    if (handoffCode || params.has("token")) {
      params.delete("doar_handoff");
      params.delete("token");
      const qs = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : "") + (window.location.hash || ""));
    }

    let cancelled = false;
    // An in-flight handoff/peer exchange/cookie refresh must never revive a
    // session the user signed out of, or overwrite a newer account selection.
    const mayApply = (originalToken) => !cancelled &&
      sessionStorage.getItem(SIGNED_OUT_KEY) !== "1" &&
      sessionStorage.getItem("doar_token") === originalToken;

    const initialise = async () => {
      let baseline = sessionStorage.getItem("doar_token");
      try {
        if (handoffCode) {
          sessionStorage.removeItem(SIGNED_OUT_KEY);
          _clearLocalAuth(false);
          baseline = null;
          const { data } = await api.post("/auth/handoff/exchange", {
            code: handoffCode,
            targetOrigin: window.location.origin,
          });
          if (!mayApply(baseline)) return;
          const access = data?.token || data?.accessToken;
          if (!applyAccessToken(access)) throw new Error("Invalid sign-in handoff token.");
          if (!cancelled) {
            const profile = data?.user;
            setUserInfo(profile && String(profile.id || profile._id || "").toLowerCase() === accessUserId(access) ? profile : null);
          }
          return;
        }

        if (sessionStorage.getItem(SIGNED_OUT_KEY) === "1") return;

        const stored = sessionStorage.getItem("doar_token");
        baseline = stored;
        let access = stored;
        if (!validPeerToken(access)) access = await requestPeerAccessToken();
        if (!mayApply(baseline)) return;
        const originalAccount = accessUserId(stored);
        // An expired A token is still an explicit A selection. Another tab
        // offering B's valid token must not switch accounts implicitly.
        if (validPeerToken(access) &&
            (!originalAccount || accessUserId(access) === originalAccount)) {
          applyAccessToken(access);
          return;
        }

        const { data } = await api.post("/auth/refresh", null, {
          ...(originalAccount ? { headers: { "X-Doar-Account-Id": originalAccount } } : {}),
          _skipAuthRefresh: true,
        });
        if (!mayApply(baseline)) return;
        const newToken = data?.token || data?.accessToken;
        if (newToken && (!originalAccount || accessUserId(newToken) === originalAccount)) {
          applyAccessToken(newToken);
        }
      } catch {
        if (mayApply(baseline)) {
          setToken(null);
          setUserInfo(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    initialise();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!token) { setUserInfo(null); return; }
    fetchMe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => () => clearTimeout(refreshTimerRef.current), []);

  const login = useCallback((access) => applyAccessToken(access), []); // eslint-disable-line

  const value = useMemo(
    () => ({
      token,
      setToken,
      userInfo,
      setUserInfo,
      loading,
      login,
      logout,
      logoutAll,
      silentRefresh,
      fetchMe,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [token, userInfo, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
